import React, { useEffect, useState } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './SuggestionsPanel.module.css';
import classnames from 'classnames';
import type AssistantMessage from '../../../framework/models/AssistantMessage';

const NextActionSuggestionsPanel: React.FC<{
  suggestions?: Record<string, string>;
  collapsible?: boolean;
  collapsed?: boolean;
  className?: string;
  afterMessage?: AssistantMessage;
}> = ({
  suggestions,
  collapsible = false,
  collapsed = false,
  className,
  afterMessage,
}) => {
  const controller = useController<AssistantViewController>();
  const { agentName, readOnly } = controller.state;

  // eslint-disable-next-line no-unused-expressions
  agentName; // Unused but triggers re-renders if the agent changes, but the agent can't be in state.

  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  useEffect(() => {
    if (collapsible) {
      setIsCollapsed(collapsed);
    }
  }, [collapsed, collapsible]);

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  if (!suggestions || readOnly) {
    return null;
  }

  const suggestionEntries = Object.entries(suggestions);
  const suggestionCount = suggestionEntries.length;

  return (
    <div
      className={classnames(styles.suggestionList, className, {
        [styles.collapsed]: isCollapsed,
      })}
    >
      <div className={styles.headerRow}>
        {collapsible && (
          <button
            className={styles.collapseButton}
            onClick={toggleCollapse}
            aria-label={
              isCollapsed ? 'Expand suggestions' : 'Collapse suggestions'
            }
          >
            <i className={`far fa-chevron-${isCollapsed ? 'right' : 'down'}`} />
          </button>
        )}
        <div
          className={classnames(styles.headerTitle, {
            [styles.clickable]: collapsible,
          })}
          onClick={toggleCollapse}
        >
          Suggestions {isCollapsed ? `(${suggestionCount})` : ''}
        </div>
      </div>

      {!isCollapsed && (
        <div className={styles.suggestionsContainer}>
          {suggestionEntries.map(([suggestion, prompt]) => (
            <React.Fragment key={suggestion}>
              <aha-button
                class={styles.suggestionButton}
                kind='gray'
                size='small'
                onClick={() =>
                  controller.actionUseSuggestion(prompt, afterMessage)
                }
              >
                <span className={styles.suggestionText}>{suggestion}</span>
                <i className='far fa-arrow-right' aria-hidden='true' />
              </aha-button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApplicationView(NextActionSuggestionsPanel);
